import React from 'react';
import { useState, useRef, useContext } from 'react'
import { VariableSizeList as List } from 'react-window'
import debounce from 'lodash.debounce';


/** Context for cross component communication */
const VirtualTableContext = React.createContext({
    top: 0,
    setTop: (value) => {},
    header: <></>,
    footer: <></>,
});

/** The virtual table. It basically accepts all of the same params as the original FixedSizeList.*/
function VirtualTable({ row, header, footer, ...rest }) {
    const listRef = useRef(null);
    const [top, setTop] = useState(0);

    const handleResize = debounce(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0, true);
        }
    }, 100);

    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    })

    React.useEffect(() => {
        // this keeps track of the code count, not row/itemCount.
        // if codeCount property changes then the table will scroll to the top.
        listRef.current && listRef.current.scrollTo(0, 0);
    }, [rest.codeCount])

    React.useEffect(() => {
        // this triggers a rerender if the height changes. 
        // ensuring the page doesn't go blank.
    }, [rest.height])

    return (
    <VirtualTableContext.Provider value={{ top, setTop, header, footer }}>
        <List
        {...rest}
        innerElementType={Inner}
        onItemsRendered={(props) => {
            const style =
            listRef.current &&
            // Accessing private method
            listRef.current._getItemStyle(props.overscanStartIndex);
            setTop((style && style.top) || 0);
            // Call the original callback
            rest.onItemsRendered && rest.onItemsRendered(props);
        }}
        ref={(el) => (listRef.current = el)}
        >
        {row}
        </List>
    </VirtualTableContext.Provider>
    );
}

/**
 * The Inner component of the virtual list. This is the "Magic".
 * Capture what would have been the top elements position and apply it to the table.
 * Other than that, render an optional header and footer.
 **/
const Inner = React.forwardRef(function Inner({ children, ...rest }, ref) {
    const { header, footer, top } = useContext(VirtualTableContext);
    return (
    <div {...rest} ref={ref}>
        <table className="fixed-table w-100 bg-light text-dark sticky-top border-bottom text-12" style={{ top, position: 'absolute', width: '100%' }}>
        {header}
        <tbody>{children}</tbody>
        {footer}
        </table>
    </div>
    );
});


export default VirtualTable;